import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

interface HoldSkin {
  id: string;
  name: string;
  description: string | null;
  rarity: Rarity;
  priceCoins: number;
  priceGems: number;
  imageUrl: string | null;
  isActive: boolean;
  isDefault: boolean;
  isStarter: boolean;
  sortOrder: number;
}

// 4 gameplay colors — preview tints so the admin sees how the grayscale
// shape recolors in-game.
const TINTS = ['#E63946', '#3B82F6', '#F4C724', '#9B5DE5'];

async function req<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method, headers,
    body: isForm ? (body as FormData) : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(d || `HTTP ${res.status}`);
  }
  return res.json();
}

export function HoldSkinsPage() {
  const [items, setItems] = useState<HoldSkin[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () =>
    req<{ items: HoldSkin[] }>('GET', '/admin/holdskins/')
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => { void load(); }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  async function createNew(isDefault: boolean) {
    setCreating(true);
    try {
      await req('POST', '/admin/holdskins/', {
        name: isDefault ? 'Default Shape' : 'New Hold Skin',
        isDefault,
        rarity: 'common',
        sortOrder: items!.length,
      });
      await load();
    } catch (e: any) { alert(e?.message); }
    setCreating(false);
  }

  const defaults = items.filter((i) => i.isDefault);
  const skins = items.filter((i) => !i.isDefault);

  return (
    <div>
      <PageTitle title="Hold Skins" />
      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.25)' }}>
        Climbing-hold shapes. Upload a <b>grayscale / white PNG silhouette</b> on a transparent
        background — the game tints it to the 4 play colors at runtime (preview swatches show this).
        <b> Default shapes</b> form the free random pool (one is picked per match when the player has no
        skin equipped); upload at least 2 for variety. <b>Skins</b> are purchasable and override the
        random when equipped. Image auto-optimized to a 256px WebP.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn" onClick={() => createNew(true)} disabled={creating}>+ DEFAULT SHAPE</button>
        <button className="btn btn-primary" onClick={() => createNew(false)} disabled={creating}>+ NEW SKIN</button>
      </div>

      <div className="tag" style={{ marginBottom: 8, color: 'var(--c-cyan)' }}>DEFAULT POOL (free · random per match)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {defaults.map((s) => <SkinRow key={s.id} item={s} onChanged={load} />)}
        {defaults.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No default shapes — game uses the built-in drawn shape.</div>}
      </div>

      <div className="tag" style={{ marginBottom: 8, color: 'var(--c-purple)' }}>PURCHASABLE SKINS</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {skins.map((s) => <SkinRow key={s.id} item={s} onChanged={load} />)}
        {skins.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No skins yet.</div>}
      </div>
    </div>
  );
}

function SkinRow({ item, onChanged }: { item: HoldSkin; onChanged: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? '');
  const [rarity, setRarity] = useState<Rarity>(item.rarity);
  const [priceCoins, setPriceCoins] = useState(item.priceCoins);
  const [priceGems, setPriceGems] = useState(item.priceGems);
  const [isActive, setIsActive] = useState(item.isActive);
  const [isStarter, setIsStarter] = useState(item.isStarter);
  const [sortOrder, setSortOrder] = useState(item.sortOrder);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await req('PATCH', `/admin/holdskins/${item.id}`, {
        name, description: description || null, rarity, priceCoins, priceGems,
        isActive, isStarter, sortOrder,
      });
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function del() {
    if (!confirm(`Delete "${item.name}"? Players who own it lose it.`)) return;
    setBusy(true);
    try { await req('DELETE', `/admin/holdskins/${item.id}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function upload(f: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await req('POST', `/admin/holdskins/${item.id}/image`, fd, true);
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function clearImg() {
    setBusy(true);
    try { await req('DELETE', `/admin/holdskins/${item.id}/image`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 14, display: 'flex', gap: 14, flexWrap: 'wrap', opacity: isActive ? 1 : 0.6 }}>
      {/* Tinted previews */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {item.imageUrl ? TINTS.map((c) => (
          <div key={c} title={c} style={{ width: 40, height: 40, background: 'rgba(0,0,0,0.35)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              width: 32, height: 32,
              backgroundColor: c,
              WebkitMaskImage: `url(${item.imageUrl})`,
              maskImage: `url(${item.imageUrl})`,
              WebkitMaskSize: 'contain', maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center', maskPosition: 'center',
            }} />
          </div>
        )) : (
          <div style={{ width: 40, height: 40, background: 'rgba(0,0,0,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--ink-dim)' }}>no img</div>
        )}
      </div>

      <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" />
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={rarity} onChange={(e) => setRarity(e.target.value as Rarity)} style={{ flex: 1 }}>
            {['common', 'rare', 'epic', 'legendary'].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input type="number" value={sortOrder} onChange={(e) => setSortOrder(+e.target.value)} title="sort order" style={{ width: 70 }} />
        </div>
        {!item.isDefault && (
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="number" value={priceCoins} onChange={(e) => setPriceCoins(+e.target.value)} placeholder="coins" style={{ flex: 1 }} />
            <input type="number" value={priceGems} onChange={(e) => setPriceGems(+e.target.value)} placeholder="gems" style={{ flex: 1 }} />
          </div>
        )}
        <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
          <label><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> active</label>
          {!item.isDefault && <label><input type="checkbox" checked={isStarter} onChange={(e) => setIsStarter(e.target.checked)} /> starter (free)</label>}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
        <input type="file" accept="image/png,image/webp,image/gif" ref={fileRef} style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>{item.imageUrl ? 'REPLACE IMG' : 'UPLOAD IMG'}</button>
        {item.imageUrl && <button className="btn" onClick={clearImg} disabled={busy} style={{ color: 'var(--c-red)' }}>CLEAR IMG</button>}
        <button className="btn btn-primary" onClick={save} disabled={busy}>SAVE</button>
        <button className="btn" onClick={del} disabled={busy} style={{ color: 'var(--c-red)' }}>DELETE</button>
      </div>
    </div>
  );
}
